import {
  BellFilled,
  CalendarFilled,
  CheckCircleFilled,
  EditOutlined,
  FrownFilled,
  MehFilled,
  PlusOutlined,
  SmileFilled,
} from '@ant-design/icons';
import Drop from 'assets/drop.svg?react';
import ModuleCard from 'components/cards/ModuleCard';
import SymptomTag from 'components/SymptomTag';
import { Button } from 'components/ui/button';
import dayjs from 'dayjs';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import SymptomTrackingSurveyModal from 'pages/SymptomTrackingSurvey';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import SymptomTrackingReminderModal from './modals/SymptomTrackingReminderModal';

// Number of past days (before yesterday) displayed as their own cards, in addition to
// the "Yesterday" and "Today" cards, so that there are 7 day-tracking cards in total.
const NUMBER_OF_ADDITIONAL_PAST_DAYS = 5;

const SEVERITY_TEXT_CLASS = {
  veryGood: 'text-chart-severity-very-good',
  good: 'text-chart-severity-good',
  average: 'text-chart-severity-average',
  bad: 'text-chart-severity-bad',
  veryBad: 'text-chart-severity-very-bad',
} as const;

// A card that scrolls horizontally with the others
const CARD_WRAPPER_CLASS = 'min-w-full snap-center';
// Section label inside a card ("Règles", "État général", "Symptômes")
const LABEL_CLASS = 'm-0 text-sm font-normal text-muted-foreground';
// Filled-out cards stretch to the full height of the scroller so the action button sits at the bottom
const FILLED_OUT_CARD_STYLE: React.CSSProperties = { position: 'relative', zIndex: 1, height: '100%' };
const FILLED_OUT_CARD_BODY_STYLE: React.CSSProperties = { height: '100%', display: 'flex', flexDirection: 'column' };

interface TrackingDataDisplayProps {
  dayData: DailyStatus | undefined;
  onEditClick: () => void;
  posthogSource?: string;
  testIdLabel: string;
}

const TrackingDataDisplay: React.FC<TrackingDataDisplayProps> = ({
  dayData,
  onEditClick,
  posthogSource = 'horizontal_cards',
  testIdLabel,
}) => {
  const { t } = useTranslation();
  const hasTracking = dayData !== undefined;

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  // Function to render general state icon with proper color and text
  const renderGeneralStateIcon = () => {
    if (dayData === undefined || dayData.general_state === null || dayData.general_state === undefined) {
      return (
        <div className="flex items-center gap-1 text-border">
          <SmileFilled />
          <span>-</span>
        </div>
      );
    }

    const value = dayData.general_state;
    let Icon: typeof SmileFilled;
    let key: keyof typeof SEVERITY_TEXT_CLASS;

    if (value === 4) {
      Icon = SmileFilled;
      key = 'veryGood';
    } else if (value === 3) {
      Icon = SmileFilled;
      key = 'good';
    } else if (value === 2) {
      Icon = MehFilled;
      key = 'average';
    } else if (value === 1) {
      Icon = FrownFilled;
      key = 'bad';
    } else {
      // value === 0
      Icon = FrownFilled;
      key = 'veryBad';
    }

    return (
      <div className="flex items-center gap-1">
        <Icon className={SEVERITY_TEXT_CLASS[key]} />
        <span className="font-bold">{t(`symptomTracking.survey.generalState.${key}`)}</span>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col justify-between">
      <div>
        <div className="mb-2 flex w-full flex-row items-center justify-between">
          {isPeriodTrackingEnabled && (
            <div className="mb-2 flex w-full flex-col">
              <h4 className={LABEL_CLASS}>{t('symptomTracking.graphs.menstruation')}</h4>
              <div className="mb-1 -ml-0.5 flex items-center">
                {Array.from({ length: 4 }, (_, index) => {
                  const dropNumber = index + 1;
                  const isActive = dayData !== undefined && dropNumber <= dayData.menstruation_amount;

                  return (
                    <Drop
                      key={index}
                      height={16}
                      width={16}
                      fill={isActive ? 'var(--chart-severity-very-bad)' : 'var(--border)'}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-2 flex w-full flex-col">
            <h4 className={LABEL_CLASS}>{t('symptomTracking.graphs.generalState')}</h4>
            {renderGeneralStateIcon()}
          </div>
        </div>

        <div className="mb-2 flex w-full flex-col">
          {dayData !== undefined && dayData?.symptoms.length > 0 ? (
            <div className="relative mb-3">
              <h4 className={LABEL_CLASS}>{t('symptomTracking.graphs.symptoms')}</h4>

              <div className="mt-1 flex w-full max-w-full flex-nowrap justify-start gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {dayData?.symptoms.map((symptom: string) => (
                  <SymptomTag key={symptom} symptom={symptom} />
                ))}
              </div>
              {/* Fade below the "Symptômes" label, over the end of the scrolling tags */}
              <div className="pointer-events-none absolute top-6 right-0 bottom-0 z-[1] w-5 bg-linear-to-l from-card to-card/0" />
            </div>
          ) : (
            <h4 className={LABEL_CLASS}>{t('symptomTracking.noSymptoms')}</h4>
          )}
        </div>
      </div>

      {/* Clickable Icon Button */}
      <div
        data-testid={`tracking.${testIdLabel}.edit`}
        className={cn(
          'mt-3 flex size-12 cursor-pointer items-center justify-center rounded-full transition-all duration-200',
          hasTracking ? 'border-2 border-primary bg-transparent' : 'bg-primary',
        )}
        onClick={() => {
          onEditClick();
          posthog.capture(dayData !== undefined ? 'tracking.survey.edit_started' : 'tracking.survey.started', {
            source: posthogSource,
          });
        }}
      >
        {dayData !== undefined ? (
          <EditOutlined className="text-2xl text-primary" />
        ) : (
          <PlusOutlined className="text-2xl text-primary-foreground" />
        )}
      </div>
    </div>
  );
};

export default function HorizontalTrackingCards({
  displayTomorrow,
  onOpenCalendar,
}: {
  displayTomorrow: boolean;
  onOpenCalendar?: () => void;
}) {
  const { t } = useTranslation();
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyDate, setSurveyDate] = useState(dayjs());
  const { hasTrackingForToday, getSpecificDailyStatus } = useSymptomTrackingData();
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  // Get today's data
  const today = dayjs();
  const todayStatus = getSpecificDailyStatus(today);

  // Get yesterday's data
  const yesterday = dayjs().subtract(1, 'day');
  const yesterdayStatus = getSpecificDailyStatus(yesterday);
  const hasYesterdayTracking = yesterdayStatus !== undefined;

  // Older days, shown before "Yesterday", oldest first
  const pastDays = Array.from({ length: NUMBER_OF_ADDITIONAL_PAST_DAYS }, (_, index) =>
    dayjs().subtract(NUMBER_OF_ADDITIONAL_PAST_DAYS - index + 1, 'day'),
  );

  // Number of cards rendered before the "Today" card, used to center the scroll view on it
  const numberOfCardsBeforeToday = (onOpenCalendar ? 1 : 0) + pastDays.length + 1;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Center on the Today card on mount
      const cardWidth = container.clientWidth * 0.7; // 70% of container width
      container.scrollLeft = cardWidth * numberOfCardsBeforeToday + 16;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDayCard = ({
    key,
    title,
    hasTracking,
    dayStatus,
    day,
    posthogSource,
    testIdLabel,
  }: {
    key?: string;
    title: React.ReactNode;
    hasTracking: boolean;
    dayStatus: DailyStatus | undefined;
    day: dayjs.Dayjs;
    posthogSource: string;
    testIdLabel: string;
  }) => (
    <div key={key} className={cn(CARD_WRAPPER_CLASS, hasTracking && 'relative')}>
      <ModuleCard
        title={
          <>
            {title}
            {hasTracking && <CheckCircleFilled className="ml-2 text-success" />}
          </>
        }
        style={hasTracking ? FILLED_OUT_CARD_STYLE : undefined}
        bodyStyle={hasTracking ? FILLED_OUT_CARD_BODY_STYLE : undefined}
      >
        <div className="flex w-full flex-1 flex-col items-start overflow-hidden pt-4">
          <TrackingDataDisplay
            dayData={dayStatus}
            onEditClick={() => {
              setSurveyDate(day);
              setIsSurveyModalOpen(true);
            }}
            posthogSource={posthogSource}
            testIdLabel={testIdLabel}
          />
        </div>
      </ModuleCard>
    </div>
  );

  return (
    <div className="relative w-full">
      {/* Fade overlay on the left */}
      <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-[5%] bg-linear-to-r from-background to-background/0" />

      {/* Fade overlay on the right */}
      <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-[5%] bg-linear-to-l from-background to-background/0" />

      {/* Horizontal padding centers the cards while showing the adjacent ones */}
      <div
        className="flex h-full w-full snap-x snap-mandatory items-stretch gap-4 overflow-x-auto scroll-smooth px-[18%] pb-4 [scrollbar-width:none]"
        ref={scrollContainerRef}
      >
        {/* Calendar Card */}
        {onOpenCalendar && (
          <div className="flex h-full min-w-fit snap-center items-center justify-center px-4">
            <div
              className="flex h-auto cursor-pointer flex-col items-center rounded-lg p-4"
              onClick={() => {
                onOpenCalendar();
                posthog.capture('tracking.calendar_opened_from_horizontal_cards');
              }}
              data-testid="tracking.calendar.open"
            >
              <CalendarFilled className="mb-2 text-2xl text-foreground" />
              <Button variant="link">{t('symptomTracking.calendar.accessFullCalendar')}</Button>
            </div>
          </div>
        )}

        {/* Past days cards (before Yesterday) */}
        {pastDays.map(day => {
          const dayStatus = getSpecificDailyStatus(day);
          const dayKey = day.format('YYYY-MM-DD');

          return renderDayCard({
            key: dayKey,
            title: <span className="capitalize">{day.format('dddd DD/MM')}</span>,
            hasTracking: dayStatus !== undefined,
            dayStatus,
            day,
            posthogSource: 'horizontal_cards_history',
            testIdLabel: dayKey,
          });
        })}

        {/* Yesterday Card */}
        {renderDayCard({
          title: t('common.yesterday'),
          hasTracking: hasYesterdayTracking,
          dayStatus: yesterdayStatus,
          day: yesterday,
          posthogSource: 'horizontal_cards_yesterday',
          testIdLabel: 'yesterday',
        })}

        {/* Today Card */}
        {renderDayCard({
          title: t('common.today'),
          hasTracking: hasTrackingForToday,
          dayStatus: todayStatus,
          day: today,
          posthogSource: 'horizontal_cards_today',
          testIdLabel: 'today',
        })}

        {/* Tomorrow Card */}
        {displayTomorrow && (
          <div className={CARD_WRAPPER_CLASS}>
            <ModuleCard title={t('common.tomorrow')}>
              <div className="flex w-full flex-1 flex-col items-start overflow-hidden pt-4">
                <p className="m-0 text-sm text-muted-foreground">{t('symptomTracking.survey.reminder.explain')}</p>
                <Button
                  variant="secondary"
                  // The label wraps onto two lines, so the button must not keep its single-line height
                  className="h-auto max-w-full whitespace-normal"
                  onClick={() => {
                    // Navigate to reminder settings or trigger reminder setup
                    setIsReminderModalOpen(true);
                    posthog.capture('tracking.reminder.activated', {
                      source: 'horizontal_cards',
                    });
                  }}
                >
                  <BellFilled />
                  {t('symptomTracking.reminder.callToAction')}
                </Button>
              </div>
            </ModuleCard>
          </div>
        )}

        <SymptomTrackingSurveyModal
          key={surveyDate.format('YYYY-MM-DD')}
          open={isSurveyModalOpen}
          onClose={() => {
            setIsSurveyModalOpen(false);
          }}
          date={surveyDate}
        />
        <SymptomTrackingReminderModal
          hidePeriodReminder
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
        />
      </div>
    </div>
  );
}
